import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Popover, MenuList, ListItemButton, ListItemIcon, SxProps, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Tool } from '../containers/plans/types';
import { translateToolName } from '../i18n/utils';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import HandymanIcon from '@mui/icons-material/Handyman';

const PegboardContainer = styled(Box)(({ theme }) => ({
	position: 'relative',
	width: '100%',
	marginTop: '8px',
	backgroundColor: theme.palette.background.paper,
	borderRadius: '8px',
	boxShadow: theme.shadows[2],
	overflow: 'hidden',
	'&::before': {
		content: '""',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='pegboard' x='0' y='0' width='40' height='40' patternUnits='userSpaceOnUse'%3E%3Ccircle cx='20' cy='20' r='3' fill='${encodeURIComponent(theme.palette.divider)}'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23pegboard)'/%3E%3C/svg%3E")`,
		backgroundSize: '40px 40px',
		backgroundPosition: 'center',
		opacity: 0.7,
	}
}));

const HeaderContainer = styled(Box)(() => ({
	display: 'flex',
	padding: '24px',
	alignItems: 'center',
}));

const ContentContainer = styled(Box)(() => ({
	position: 'relative',
	zIndex: 1,
	display: 'flex',
	justifyContent: 'center',
	flexWrap: 'wrap',
	padding: '10px 10px 20px',
	gap: '0 40px'
}));

const ToolContainer = styled(Box)(({ theme }) => ({
	height: '120px',
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	transition: 'transform 0.2s ease-in-out',
	[theme.breakpoints.up('md')]: {
		height: '160px',
	},
}));

const ToolImagePlaceholder = () => {
	const theme = useTheme();	
	return (
		<Box sx={{ width: '80px', height: '80px', [theme.breakpoints.up('md')]: { width: '120px', height: '120px' }, color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
			<HandymanIcon sx={{ fontSize: '48px' }} />
		</Box>
	);
};

const ToolImage = styled('img')(({ theme }) => ({
	width: '80px',
	height: '80px',
	[theme.breakpoints.up('md')]: {
		width: '120px',
		height: '120px',
	},
	objectFit: 'contain',
	marginBottom: '8px',
}));

const ToolName = styled(Typography)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	justifyContent: 'center',
	fontSize: '0.875rem',
	textAlign: 'center',
	color: theme.palette.text.secondary,
	zIndex: 1,
}));

/**
 * @deprecated Use ToolShedV2 instead
 */
interface ToolShedProps {
	children?: React.ReactNode;
	tools?: (Tool | {key: string})[];
	companyToolsSet?: Set<number>;
	title?: string;
	onAddTool?: (toolId: number) => void;
	onRemoveTool?: (toolId: number) => void;
	sx?: SxProps;
}

interface MenuItem {
	label: string;
	icon?: React.ReactNode;
	onClick: (item: object) => void;
}

interface ToolShedV2Props {
	children?: React.ReactNode;
	tools?: (Tool & { disabled?: boolean })[];
	title?: string;
	sx?: SxProps;
	menuItemsFn?: (tool: Tool) => MenuItem[];
}


/**
 * @deprecated Use ToolShedV2 instead
 */
const ToolShed: React.FC<ToolShedProps> = ({ 
	children,
	tools,
	companyToolsSet,
	title,
	sx,
	onAddTool,
	onRemoveTool,
}) => {
	const [openId, setOpenId] = useState<number | null>(null);
	const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
	const {t} = useTranslation();

	const handleToolClick = (el: HTMLDivElement, tool: Tool) => {
		if (tool.id === selectedTool?.id) {
			setOpenId(tool.id);
		}
		setAnchorEl(el);
		setSelectedTool(tool);
	};

	useEffect(() => {
		if (selectedTool) {
			setOpenId(selectedTool.id);
		}
	}, [selectedTool]);

	const isInteractive = useMemo(() => Boolean(onAddTool || onRemoveTool), [onAddTool, onRemoveTool]);

	return (
		<>
			<PegboardContainer sx={sx}>
				{title && <HeaderContainer>
					<Typography variant="h6" component="h2">
						{title}
					</Typography>
				</HeaderContainer>}
				<ContentContainer>
					{tools && tools.map((tool, i) => (
						<ToolContainer 
							key={tool.key} 
							onClick={(isInteractive && (tool as Tool).id) ? (e: React.MouseEvent<HTMLDivElement>) => handleToolClick(e.currentTarget, tool as Tool) : undefined} 
							sx={{cursor: (isInteractive && (tool as Tool).id) ? 'pointer' : 'default'}}
						>
							{(tool as Tool).imgUrl ? <ToolImage 
								src={(tool as Tool).imgUrl} 
								alt={tool.key} 
								sx={companyToolsSet && {
									filter: !(tool as Tool).id || companyToolsSet?.has((tool as Tool).id || i) ? 'opacity(1)' : 'opacity(0.3)'
								}}
							/> : <ToolImagePlaceholder />}
							<ToolName variant="body2">{translateToolName(tool.key)}</ToolName>
						</ToolContainer>
					))}
					{children}
				</ContentContainer>
			</PegboardContainer>
			<Popover
				open={openId !== null}
				onClose={() => setOpenId(null)}
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<MenuList sx={{ width: '200px' }}>
					{!companyToolsSet?.has(selectedTool?.id as number) ? (
						<ListItemButton onClick={() => onAddTool?.(openId as number)}>
							<ListItemIcon>
								<AddIcon />
							</ListItemIcon>
							<Typography variant="body1">{t('toolShed.addTool')}</Typography>
						</ListItemButton>
					) : (
						<ListItemButton onClick={() => onRemoveTool?.(openId as number)}>
							<ListItemIcon>
								<RemoveIcon />
							</ListItemIcon>
							<Typography variant="body1">{t('toolShed.removeTool')}</Typography>
						</ListItemButton>
					)}
				</MenuList>
			</Popover>
		</>
	);
};

export const ToolShedV2: React.FC<ToolShedV2Props> = ({ 
	children,
	tools,
	title,
	sx,
	menuItemsFn
}) => {
	const [openId, setOpenId] = useState<number | null>(null);
	const [selectedTool, setSelectedTool] = useState<Tool & { disabled?: boolean } | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

	const handleToolClick = (el: HTMLDivElement, tool: Tool) => {
		if (tool.id === selectedTool?.id) {
			setOpenId(tool.id);
		}
		setAnchorEl(el);
		setSelectedTool(tool);
	};

	const handleMenuItemClick = (menuItem: MenuItem) => {
		if (menuItem.onClick && selectedTool) {
			menuItem.onClick(selectedTool);
		}
		setOpenId(null);
	};

	useEffect(() => {
		if (selectedTool) {
			setOpenId(selectedTool.id);
		}
	}, [selectedTool]);

	const isInteractive = useMemo(() => Boolean(menuItemsFn), [menuItemsFn]);
	const menuItems = useMemo(
		() => (selectedTool && menuItemsFn ? menuItemsFn(selectedTool) : []),
		[menuItemsFn, selectedTool]
	);

	return (
		<>
			<PegboardContainer sx={sx}>
				{title && <HeaderContainer>
					<Typography variant="h6" component="h2">
						{title}
					</Typography>
				</HeaderContainer>}
				<ContentContainer>
					{tools && tools.map((tool) => (
						<ToolContainer 
							key={tool.id} 
							onClick={isInteractive ? (e: React.MouseEvent<HTMLDivElement>) => handleToolClick(e.currentTarget, tool) : undefined} 
							sx={{cursor: isInteractive ? 'pointer' : 'default'}}
						>
							<ToolImage 
								src={tool.imgUrl} 
								alt={tool.key} 
								sx={{
									filter: tool.disabled ? 'opacity(0.3)' : 'opacity(1)',
								}}
							/>
							<ToolName variant="body2">{translateToolName(tool.key)}</ToolName>
						</ToolContainer>
					))}
					{children}
				</ContentContainer>
			</PegboardContainer>
			<Popover

				open={openId !== null}
				onClose={() => setOpenId(null)}
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<MenuList sx={{ width: '200px' }}>
					{menuItems?.map((item) => (
						<ListItemButton key={item.label} onClick={() => handleMenuItemClick(item)}>
							{item.icon && <ListItemIcon>
								{item.icon}
							</ListItemIcon>}
							<Typography variant="body1">{item.label}</Typography>
						</ListItemButton>
					))}
				</MenuList>
			</Popover>
		</>
	);
};

export default ToolShed; 